/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import HttpBackend from "i18next-http-backend";
import { safeLocalStorage } from "@/helper/safeLocalStorage";
import { ALL_LANGUAGES, type SupportedLanguage } from "@/lib/constants";
import { normalizeLocalizedPath } from "@/utils/localizedPath";

// DD-12: Re-export for backward compatibility — existing callers import from here
export const SUPPORTED_LANGUAGES = ALL_LANGUAGES;
export type { SupportedLanguage };

// Safely get stored language with fallback — uses safeLocalStorage for consistent
// SSR safety, error handling, and Sentry reporting across the codebase.
const getStoredLanguage = (): SupportedLanguage => {
  const stored = safeLocalStorage.getItem("lang");
  if (stored && SUPPORTED_LANGUAGES.includes(stored as SupportedLanguage)) {
    return stored as SupportedLanguage;
  }
  return "en";
};

// Detect language from URL path (e.g., /de/product-page)
const getLanguageFromPath = (): SupportedLanguage | null => {
  if (typeof window !== "undefined") {
    const { lang } = normalizeLocalizedPath(window.location.pathname);
    return lang as SupportedLanguage | null;
  }
  /* c8 ignore next 2 */
  // SSR guard: window is always defined in jsdom and in the browser;
  // this branch only fires in true server-side environments.
  return null;
};

// Get initial language: URL path > localStorage > default
const getInitialLanguage = (): SupportedLanguage => {
  return getLanguageFromPath() || getStoredLanguage();
};

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Load locale files dynamically from public folder
    backend: {
      loadPath: "/locales/{{lng}}.json",
    },
    lng: getInitialLanguage(),
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,

    // Only load the current language, not all languages
    preload: [], // Don't preload any languages

    // Loading behavior
    load: "currentOnly", // Only load current language, not language variants

    interpolation: {
      escapeValue: false,
    },

    // React specific options
    react: {
      useSuspense: true, // Enable Suspense for loading states
    },

    // Debug mode (disable in production)
    debug: import.meta.env.DEV,
  });

export default i18n;
